
import React, { useMemo, useState } from "react";

import LeftMenu from "../../../components/LeftMenu";

import CmsPageSection from "./CmsPageSection";

import CmsEvaEditor, {
    DEFAULT_EVA_SETTINGS
} from "./CmsEvaEditor";

import "./CmsSiteEditor.css";


//========================================================
// Initial Demo Sections
//========================================================

const initialSections = [
    {
        id: "hero",
        title: "Premium section",
        blocks: [
            {
                id: "tour",
                type: "HERO",
                width: 4,
                title: "Explore Your Workplace",
                description:
                    "Take an immersive tour of your building, its facilities and services.",
                image: "/eva/images/square_website1.png",
                link: "/eva"
            }
        ]
    },
    {
        id: "content",
        title: "Homepage content",
        blocks: [
            {
                id: "meeting",
                type: "CARD",
                width: 2,
                title: "Meeting Rooms",
                description: "Explore and book the spaces you need.",
                image: "/eva/images/square_website2.png",
                link: "#"
            },
            {
                id: "docs",
                type: "DOCUMENT",
                width: 1,
                title: "Building Handbook",
                description: "Policies and guidance from SharePoint.",
                link: "#"
            },
            {
                id: "welcome",
                type: "TEXT",
                width: 1,
                title: "Welcome",
                description:
                    "Everything you need to know about your workplace."
            }
        ]
    }
];


//========================================================
// Create New Block
//========================================================

const newBlock = (type) => {

    const titles = {
        CARD: "New navigation card",
        TEXT: "New text section",
        IMAGE: "New image",
        EMBED: "New embed",
        DOCUMENT: "SharePoint document",
        LINK: "New link",
        HERO: "Featured experience",
        EVA_INTERACTION: "Ask Eva"
    };

    return {
        id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,

        type: type,

        width:
            type === "HERO"
                ? 4
                : type === "EVA_INTERACTION"
                    ? 2
                    : 1,

        title: titles[type] || "New component",

        description:
            type === "EVA_INTERACTION"
                ? DEFAULT_EVA_SETTINGS.welcomeMessage
                : "Click Edit to customise this content.",

        image:
            ["IMAGE", "CARD", "HERO"].includes(type)
                ? "/eva/images/square_website1.png"
                : "",

        link: "",

        evaSettings:
            type === "EVA_INTERACTION"
                ? { ...DEFAULT_EVA_SETTINGS }
                : undefined
    };
};


//========================================================
// CMS Site Editor
//========================================================

export default function CmsSiteEditor({
    site: incomingSite,
    onBack
}) {

    //----------------------------------------------------
    // State
    //----------------------------------------------------

    const [site, setSite] = useState({
        id: incomingSite?.id ?? 2,

        siteType: incomingSite?.siteType ?? "WEBSITE",

        title: incomingSite?.title ?? "Building Portal",

        description:
            incomingSite?.description ??
            "Building information, facilities, documents and occupant services.",

        image:
            incomingSite?.image ??
            "/eva/images/square_website1.png",

        url:
            incomingSite?.url ??
            "https://portal.example.com"
    });

    const [sections, setSections] = useState(initialSections);

    const [editing, setEditing] = useState(null);

    const [dragged, setDragged] = useState(null);

    const [notice, setNotice] = useState("");
	
	
	const [showPreview, setShowPreview] = useState(false);

    const isEva = site.siteType === "EVA";

    const blockCount = useMemo(() => {

        return sections.reduce(
            (total, section) => total + section.blocks.length,
            0
        );

    }, [sections]);


    //----------------------------------------------------
    // Site Information
    //----------------------------------------------------

    const changeSite = (key, value) => {

        setSite(previous => ({
            ...previous,
            [key]: value
        }));
    };


    //----------------------------------------------------
    // Section / Block Updates
    //----------------------------------------------------

    const updateSection = (sectionId, updateFunction) => {

        setSections(previous => {

            return previous.map(section => {

                if (section.id !== sectionId) {
                    return section;
                }

                return {
                    ...section,
                    blocks: updateFunction(section.blocks)
                };
            });
        });
    };


    const updateBlock = (sectionId, blockId, patch) => {

        updateSection(sectionId, blocks => {

            return blocks.map(block => {

                if (block.id !== blockId) {
                    return block;
                }

                return {
                    ...block,
                    ...patch
                };
            });
        });
    };


    //----------------------------------------------------
    // Remove Block
    //----------------------------------------------------

    const removeBlock = (sectionId, blockId) => {

        updateSection(
            sectionId,
            blocks => blocks.filter(block => block.id !== blockId)
        );

        if (editing?.blockId === blockId) {
            setEditing(null);
        }
    };


    //----------------------------------------------------
    // Add Block
    //----------------------------------------------------

    const addBlock = (sectionId, type) => {

        const block = newBlock(type);

        updateSection(
            sectionId,
            blocks => [...blocks, block]
        );

        setEditing({
            sectionId: sectionId,
            blockId: block.id
        });
    };


    //----------------------------------------------------
    // Drag And Drop
    //----------------------------------------------------

    const moveBlock = (targetSectionId, targetBlockId) => {

        if (!dragged) {
            return;
        }

        if (
            dragged.sectionId === targetSectionId &&
            dragged.blockId === targetBlockId
        ) {
            return;
        }

        const sourceSection = sections.find(
            section => section.id === dragged.sectionId
        );

        const block = sourceSection?.blocks.find(
            item => item.id === dragged.blockId
        );

        if (!block) {
            return;
        }

        setSections(previous => {

            return previous.map(section => {

                const blocks = section.blocks.filter(
                    item => item.id !== block.id
                );

                if (section.id === targetSectionId) {

                    const index = targetBlockId
                        ? blocks.findIndex(item => item.id === targetBlockId)
                        : blocks.length;

                    blocks.splice(
                        index < 0 ? blocks.length : index,
                        0,
                        block
                    );
                }

                return {
                    ...section,
                    blocks: blocks
                };
            });
        });

        setDragged(null);
    };


    //----------------------------------------------------
    // Active Editing Block
    //----------------------------------------------------

    const activeBlock = editing
        ? sections
            .find(section => section.id === editing.sectionId)
            ?.blocks.find(block => block.id === editing.blockId)
        : null;


    //----------------------------------------------------
    // Image Upload
    //----------------------------------------------------

    const handleUpload = (event) => {

        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        if (!file.type.startsWith("image/")) {

            setNotice("Please choose an image file.");

            return;
        }

        const reader = new FileReader();

        reader.onload = () => {

            changeSite("image", reader.result);
        };

        reader.readAsDataURL(file);
    };


    //----------------------------------------------------
    // Save Demo
    //----------------------------------------------------

    const saveDemo = () => {

        try {

            localStorage.setItem(
                `cms-demo-${site.id}`,
                JSON.stringify({
                    site: site,
                    sections: sections
                })
            );

            setNotice("Demo changes saved in this browser.");

        } catch (error) {

            setNotice(
                "Browser storage is unavailable; changes remain on screen."
            );
        }
    };


    //----------------------------------------------------
    // Restore Demo
    //----------------------------------------------------

    const restoreDemo = () => {

        try {

            const saved = localStorage.getItem(
                `cms-demo-${site.id}`
            );

            const data = JSON.parse(saved || "null");

            if (data) {

                setSite(data.site);

                setSections(data.sections);

                setEditing(null);

                setNotice("Saved demo restored.");

            } else {

                setNotice("No saved demo for this site yet.");
            }

        } catch (error) {

            setNotice("Unable to restore saved demo.");
        }
    };


    //----------------------------------------------------
    // Add Section
    //----------------------------------------------------

    const addSection = () => {

        setSections(previous => [
            ...previous,
            {
                id: `section-${Date.now()}`,
                title: "New section",
                blocks: []
            }
        ]);
    };


    //----------------------------------------------------
    // Move Section
    //----------------------------------------------------

    const moveSection = (sectionId, direction) => {

        setSections(previous => {

            const next = [...previous];

            const index = next.findIndex(
                section => section.id === sectionId
            );

            const targetIndex = index + direction;

            if (
                index < 0 ||
                targetIndex < 0 ||
                targetIndex >= next.length
            ) {
                return previous;
            }

            [next[index], next[targetIndex]] = [
                next[targetIndex],
                next[index]
            ];

            return next;
        });
    };


    //----------------------------------------------------
    // Render
    //----------------------------------------------------

    return (

        <div className="cms-editor">

            <div className="cms-editor-background" />

            <main className="cms-editor-content">

                {/*==========================================
                   Page Heading
                ==========================================*/}

                <header className="cms-editor-heading">

                    <div>

                        <button
                            type="button"
                            className="cms-editor-back"
                            onClick={
                                onBack ||
                                (() => window.history.back())
                            }
                        >
                            ← Content Management
                        </button>

                        <div className="cms-editor-eyebrow">
                            OMNISCAN / CMS ADMIN
                        </div>

                        <h1>{site.title}</h1>

                        <p>
                            Visual website editor · {blockCount} components
                            · Frontend demonstration
                        </p>

                    </div>

                    <div className="cms-editor-actions">
					<button
					    type="button"
					    className="cms-editor-preview-button"
					    onClick={() => setShowPreview(true)}
					>
					    ◉ Preview
					</button>
                        <button
                            type="button"
                            onClick={restoreDemo}
                        >
                            Restore Demo
                        </button>

                        <button
                            type="button"
                            className="cms-editor-primary"
                            onClick={saveDemo}
                        >
                            Save Demo
                        </button>

                    </div>

                </header>


                {/*==========================================
                   Notifications
                ==========================================*/}

                {notice && (

                    <div
                        className="cms-editor-notice"
                        role="status"
                    >

                        {notice}

                        <button
                            type="button"
                            onClick={() => setNotice("")}
                        >
                            ×
                        </button>

                    </div>
                )}


                {/*==========================================
                   Site Information
                ==========================================*/}

                <section className="cms-editor-panel">

                    <div className="cms-editor-panel-title">

                        <div>

                            <h2>Site information</h2>

                            <p>
                                Controls the card shown on the
                                Content Management entry screen.
                            </p>

                        </div>

                        <span className="cms-editor-pill">
                            {site.siteType}
                        </span>

                    </div>

                    <div className="cms-editor-site-fields">

                        <div className="cms-editor-fields">

                            <label>

                                Site name

                                <input
                                    value={site.title}
                                    onChange={event =>
                                        changeSite(
                                            "title",
                                            event.target.value
                                        )
                                    }
                                />

                            </label>

                            <label>

                                Description

                                <textarea
                                    rows={3}
                                    value={site.description}
                                    onChange={event =>
                                        changeSite(
                                            "description",
                                            event.target.value
                                        )
                                    }
                                />

                            </label>

                            <label>

                                Website address

                                <input
                                    value={site.url}
                                    onChange={event =>
                                        changeSite(
                                            "url",
                                            event.target.value
                                        )
                                    }
                                />

                            </label>

                        </div>


                        {/* Entry Card Image */}

                        <div className="cms-editor-image-field">

                            <span>Entry card image</span>

                            <div
                                className={
                                    `cms-editor-entry-preview ${
                                        isEva
                                            ? "cms-editor-entry-preview-eva"
                                            : ""
                                    }`
                                }
                            >

                                <img
                                    src={site.image}
                                    alt="Entry card preview"
                                />

                                <div>

                                    <strong>
                                        {site.title}
                                    </strong>

                                    <small>
                                        {site.description}
                                    </small>

                                </div>

                            </div>

                            <label className="cms-editor-upload">

                                Upload image

                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleUpload}
                                />

                            </label>

                            <small>
                                Preview only; image upload will use
                                the CMS resource service later.
                            </small>

                        </div>

                    </div>

                </section>


                {/*==========================================
                   Homepage Builder Heading
                ==========================================*/}

                <div className="cms-editor-builder-heading">

                    <div>

                        <h2>Homepage builder</h2>

                        <p>
                            Drag blocks to reorder. Choose 25%, 50%,
                            75% or 100% width. Edit the content in place.
                        </p>

                    </div>

                    <button
                        type="button"
                        onClick={addSection}
                    >
                        + Add section
                    </button>

                </div>


                {/*==========================================
                   Homepage Sections
                ==========================================*/}

                {sections.map((section, index) => (

                    <CmsPageSection
                        key={section.id}
                        section={section}
                        index={index}

                        onTitleChange={title => {

                            setSections(previous => {

                                return previous.map(item => {

                                    if (item.id !== section.id) {
                                        return item;
                                    }

                                    return {
                                        ...item,
                                        title: title
                                    };
                                });
                            });
                        }}

                        onAdd={type =>
                            addBlock(section.id, type)
                        }

                        onEdit={blockId =>
                            setEditing({
                                sectionId: section.id,
                                blockId: blockId
                            })
                        }

                        onWidth={(blockId, width) =>
                            updateBlock(
                                section.id,
                                blockId,
                                { width: width }
                            )
                        }

                        onDelete={blockId =>
                            removeBlock(section.id, blockId)
                        }

                        onDragStart={blockId =>
                            setDragged({
                                sectionId: section.id,
                                blockId: blockId
                            })
                        }

                        onDrop={blockId =>
                            moveBlock(section.id, blockId)
                        }

                        onDragEnd={() =>
                            setDragged(null)
                        }

                        onMoveSection={direction =>
                            moveSection(section.id, direction)
                        }

                        onDeleteSection={() =>
                            setSections(previous =>
                                previous.filter(
                                    item => item.id !== section.id
                                )
                            )
                        }
                    />

                ))}


                {/*==========================================
                   Add Another Section
                ==========================================*/}

                <button
                    type="button"
                    className="cms-editor-add-section"
                    onClick={addSection}
                >
                    + Add another section
                </button>

            </main>


            {/*==============================================
               Component Editor Modal
            ==============================================*/}

            {activeBlock && (

                <div
                    className="cms-editor-modal-backdrop"
                    onMouseDown={event => {

                        if (event.target === event.currentTarget) {
                            setEditing(null);
                        }
                    }}
                >

                    <div
                        className="cms-editor-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Edit component"
                    >

                        <div className="cms-editor-modal-heading">

                            <h2>
                                {activeBlock.type === "EVA_INTERACTION"
                                    ? "Configure Eva Interaction"
                                    : `Edit ${activeBlock.type.toLowerCase()} component`
                                }
                            </h2>

                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                            >
                                ×
                            </button>

                        </div>


                        {/*==================================
                           Eva-Specific Editor
                        ==================================*/}

                        {activeBlock.type === "EVA_INTERACTION" && (

                            <CmsEvaEditor
                                block={activeBlock}
                                onChange={patch =>
                                    updateBlock(
                                        editing.sectionId,
                                        activeBlock.id,
                                        patch
                                    )
                                }
                            />

                        )}


                        {/*==================================
                           Standard Component Editor
                        ==================================*/}

                        {activeBlock.type !== "EVA_INTERACTION" && (

                            <>

                                <label>

                                    Title

                                    <input
                                        value={activeBlock.title || ""}
                                        onChange={event =>
                                            updateBlock(
                                                editing.sectionId,
                                                activeBlock.id,
                                                {
                                                    title: event.target.value
                                                }
                                            )
                                        }
                                    />

                                </label>


                                <label>

                                    Text / description

                                    <textarea
                                        rows={5}
                                        value={activeBlock.description || ""}
                                        onChange={event =>
                                            updateBlock(
                                                editing.sectionId,
                                                activeBlock.id,
                                                {
                                                    description:
                                                        event.target.value
                                                }
                                            )
                                        }
                                    />

                                </label>


                                {["CARD", "HERO", "IMAGE"].includes(
                                    activeBlock.type
                                ) && (

                                    <label>

                                        Image URL

                                        <input
                                            value={activeBlock.image || ""}
                                            onChange={event =>
                                                updateBlock(
                                                    editing.sectionId,
                                                    activeBlock.id,
                                                    {
                                                        image:
                                                            event.target.value
                                                    }
                                                )
                                            }
                                        />

                                    </label>

                                )}


                                {[
                                    "CARD",
                                    "HERO",
                                    "DOCUMENT",
                                    "LINK",
                                    "EMBED"
                                ].includes(activeBlock.type) && (

                                    <label>

                                        {activeBlock.type === "EMBED"
                                            ? "Embed URL"
                                            : "Destination / document URL"
                                        }

                                        <input
                                            value={activeBlock.link || ""}
                                            onChange={event =>
                                                updateBlock(
                                                    editing.sectionId,
                                                    activeBlock.id,
                                                    {
                                                        link:
                                                            event.target.value
                                                    }
                                                )
                                            }
                                        />

                                    </label>

                                )}

                            </>

                        )}


                        {/*==================================
                           Shared Width Control
                        ==================================*/}

                        <label>

                            Width

                            <select
                                value={activeBlock.width}
                                onChange={event =>
                                    updateBlock(
                                        editing.sectionId,
                                        activeBlock.id,
                                        {
                                            width: Number(
                                                event.target.value
                                            )
                                        }
                                    )
                                }
                            >

                                {[1, 2, 3, 4].map(width => (

                                    <option
                                        key={width}
                                        value={width}
                                    >
                                        {width * 25}%
                                    </option>

                                ))}

                            </select>

                        </label>


                        {/*==================================
                           Modal Footer
                        ==================================*/}

                        <div className="cms-editor-modal-footer">

                            <button
                                type="button"
                                className="cms-editor-primary"
                                onClick={() => setEditing(null)}
                            >
                                Done
                            </button>

                        </div>

                    </div>

                </div>

            )}


            {/*==============================================
               OmniScan Navigation
            ==============================================*/}

			{/*==============================================
			   Website Preview
			==============================================*/}

			{showPreview && (

			    <div className="cms-site-preview-overlay">

			        <div className="cms-site-preview-toolbar">

			            <div>
			                <strong>{site.title}</strong>
			                <span>Website Preview</span>
			            </div>

			            <button
			                type="button"
			                onClick={() => setShowPreview(false)}
			            >
			                ✕ Close Preview
			            </button>

			        </div>

			        <div className="cms-site-preview-content">

			            <div className="cms-site-preview-heading">

			                <h1>{site.title}</h1>

			                <p>{site.description}</p>

			            </div>

			            {sections.map(section => (

			                <div
			                    key={section.id}
			                    className="cms-site-preview-section"
			                >

			                    <div className="cms-site-preview-grid">

			                        {section.blocks.map(block => (

			                            <div
			                                key={block.id}
			                                className="cms-site-preview-item"
			                                style={{
			                                    gridColumn: `span ${block.width || 1}`
			                                }}
			                            >

			                                <div className="cms-site-preview-block">

			                                    {block.image && (
			                                        <img
			                                            src={block.image}
			                                            alt={block.title}
			                                        />
			                                    )}

			                                    <div className="cms-site-preview-block-body">

			                                        <h3>{block.title}</h3>

			                                        <p>{block.description}</p>

			                                    </div>

			                                </div>

			                            </div>

			                        ))}

			                    </div>

			                </div>

			            ))}

			        </div>

			    </div>

			)}
			
			
            <LeftMenu />

        </div>

    );
}
